import React, { useState } from 'react';
import { Puzzle, AlertCircle } from 'lucide-react';
import IntegrationCard from '../components/integrations/IntegrationCard';
import IntegrationForm from '../components/integrations/IntegrationForm';
import IntegrationLogs from '../components/integrations/IntegrationLogs';
import type { IntegrationProvider, IntegrationConfig } from '../types/integrations';

const Integrations: React.FC = () => {
  const [selectedProvider, setSelectedProvider] = useState<IntegrationProvider | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<IntegrationConfig | null>(null);
  const [showLogs, setShowLogs] = useState(false);

  // Mock providers data
  const providers: IntegrationProvider[] = [
    {
      id: 'sefaz',
      name: 'SEFAZ',
      type: 'fiscal',
      description: 'Emissão e gestão de notas fiscais eletrônicas (NF-e e NFC-e).',
      docsUrl: 'https://docs.sefaz.gov.br'
    },
    {
      id: 'gerencianet',
      name: 'Gerencianet',
      type: 'payment',
      description: 'Gateway de pagamentos com suporte a Pix, boleto e cartão.',
      docsUrl: 'https://gerencianet.com.br/docs'
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp Business API',
      type: 'messaging',
      description: 'Envio automático de mensagens e notificações via WhatsApp.',
      docsUrl: 'https://business.whatsapp.com/api'
    }
  ];

  // Mock configurations data
  const configurations: IntegrationConfig[] = [
    {
      id: '1',
      providerId: 'sefaz',
      status: 'active',
      credentials: {
        certificateFile: 'certificate.pfx',
        password: '********'
      },
      settings: {
        environment: 'production',
        validateSchema: true,
        timeout: 30
      },
      createdAt: new Date(),
      updatedAt: new Date()
    }
  ];

  const handleSave = async (config: IntegrationConfig) => {
    console.log('Saving configuration:', config);
    // Here you would save the configuration to your backend
  };

  const handleTest = async (config: IntegrationConfig) => {
    console.log('Testing configuration:', config);
    // Here you would test the integration
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Puzzle className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">
              Integrações
            </h1>
          </div>
        </div>

        {selectedProvider ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  setSelectedProvider(null);
                  setSelectedConfig(null);
                }}
                className="text-blue-600 hover:text-blue-800"
              >
                ← Voltar para lista
              </button>
              <button
                onClick={() => setShowLogs(!showLogs)}
                className="text-gray-600 hover:text-gray-800"
              >
                {showLogs ? 'Ocultar logs' : 'Ver logs'}
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <IntegrationForm
                  provider={selectedProvider}
                  initialData={selectedConfig}
                  onSave={handleSave}
                  onTest={handleTest}
                />
              </div>
              {showLogs && (
                <div className="lg:col-span-1">
                  <IntegrationLogs integrationId={selectedConfig?.id} />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    As integrações permitem que o sistema se conecte com serviços externos.
                    Configure apenas os serviços que você utiliza.
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {providers.map((provider) => {
                const config = configurations.find(c => c.providerId === provider.id);
                return (
                  <IntegrationCard
                    key={provider.id}
                    provider={provider}
                    config={config}
                    onClick={() => {
                      setSelectedProvider(provider);
                      setSelectedConfig(config || null);
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Integrations;